import { Component, Input, OnInit, ElementRef, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { get } from 'lodash';
import { environment } from 'src/environments/environment';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

export interface TableColumn {
  field: string;
  headerName: string;
  sortable?: boolean;
  renderCell?: (row: any) => string;
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit {
  @Input() url: string = '';
  @Input() method: 'GET' | 'POST' = 'GET';
  @Input() columns: TableColumn[] = [];

  /** Path to list in response (e.g. 'output.list', 'data.list', etc.) */
  @Input() responseListPath: string = 'data.list';

  /** Path to TOTAL in response. If not set, we'll try common paths incl. 'output.resultCount'. */
  @Input() responseTotalPath: string | null = null;

  @Input() requestBody: any = {};
  @Input() pageLimit: number = 5;
  @Input() onRowClick?: (row: any) => void;

  @ViewChild('exportTable') exportTableRef!: ElementRef;

  baseUrl: string = environment.API_URL;
  lastResponse: any = null;
  data: any[] = [];
  total: number = 0;
  page: number = 1;

  sortField: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.fetchData();
  }

  /** ---------------- Utils ---------------- */
  extractTextFromSafeHtml(safeHtml: any): string {
    const div = document.createElement('div');
    div.innerHTML =
      safeHtml?.changingThisBreaksApplicationSecurity || safeHtml || '';
    return div.textContent || div.innerText || '';
  }

  stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent || div.innerText || '';
  }

  public fetchDataFromApi(): void {
    this.fetchData();
  }

  downloadExcel(): void {
    const tableElement = this.exportTableRef.nativeElement;
    const worksheet: XLSX.WorkSheet = XLSX.utils.table_to_sheet(tableElement, {
      raw: true,
    });
    const workbook: XLSX.WorkBook = {
      Sheets: { Sheet1: worksheet },
      SheetNames: ['Sheet1'],
    };
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/octet-stream',
    });
    FileSaver.saveAs(blob, 'table-export.xlsx');
  }

  handleRowClick(event: MouseEvent, row: any): void {
    const selectedText = window.getSelection()?.toString();
    if (!selectedText) this.onRowClick?.(row);
  }

  /** Scroll the PAGE (window) to the very top */
  private scrollToPageTop(): void {
    const opts: ScrollToOptions = { top: 0, left: 0 };
    try {
      window.scrollTo(opts);
      // extra safety for some browsers/layouts
      document.documentElement?.scrollTo?.(opts);
      document.body?.scrollTo?.(opts);
    } catch {
      window.scrollTo(0, 0);
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    }
  }

  /** Resolve total from API response smartly (supports output.resultCount, etc.) */
  private resolveTotalFromResponse(res: any): number {
    const candidates = this.responseTotalPath
      ? [this.responseTotalPath]
      : [
          'output.resultCount',
          'output.total',
          'output.count',
          'data.total',
          'data.count',
          'data.resultCount',
          'meta.total',
          'pagination.total',
          'total',
          'count',
          'resultCount',
        ];

    for (const p of candidates) {
      const v = get(res, p);
      if (typeof v === 'number' && !isNaN(v)) return v;
      if (typeof v === 'string' && v.trim() !== '' && !isNaN(+v)) return +v;
    }

    const list = get(res, this.responseListPath, []);
    return Array.isArray(list) ? list.length : 0;
  }

  totalPages(): number {
    const pages = Math.ceil((this.total || 0) / Number(this.pageLimit || 1));
    return Math.max(1, pages);
  }

  onPageLimitChange(event: any): void {
    this.pageLimit = Number(event.target?.value ?? this.pageLimit);
    this.onPageChange(1); // reset to first page
  }

  paginationArray(): (number | string)[] {
    const total = this.totalPages();
    const current = this.page;
    const pages: (number | string)[] = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      if (current <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', total);
      } else if (current >= total - 3) {
        pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
      } else {
        pages.push(1, '...', current - 1, current, current + 1, '...', total);
      }
    }
    return pages;
  }

  /** ---------------- Data Fetch ---------------- */
  fetchData(): void {
    const sortParam = this.sortField
      ? this.sortDirection === 'asc'
        ? this.sortField
        : `-${this.sortField}`
      : null;

    if (this.method === 'POST') {
      const body: any = {
        ...(this.requestBody || {}),
        page: this.page,
        pageLimit: Number(this.pageLimit),
      };
      if (sortParam) body.sort = sortParam;

      this.http.post(this.baseUrl + this.url, body).subscribe((res: any) => {
        this.lastResponse = res;
        this.data = get(res, this.responseListPath, []);
        this.total = this.resolveTotalFromResponse(res);
      });
    } else {
      let params = new HttpParams()
        .set('page', String(this.page))
        .set('limit', String(this.pageLimit));
      if (sortParam) params = params.set('sort', sortParam);

      this.http
        .get(this.baseUrl + this.url, { params })
        .subscribe((res: any) => {
          this.lastResponse = res;
          this.data = get(res, this.responseListPath, []);
          this.total = this.resolveTotalFromResponse(res);
        });
    }
  }

  /** ---------------- Sorting & Paging ---------------- */
  onSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.fetchData();
  }

  onPageChange(page: number): void {
    if (typeof page !== 'number' || isNaN(page)) return;
    const maxPage = this.totalPages();
    if (page < 1 || page > maxPage) return;

    this.page = page;
    this.fetchData();

    // Scroll the whole page to top after pagination click
    setTimeout(() => this.scrollToPageTop(), 0);
  }
}
